import {AutogenLayoutDriverFuelCardQuickInfo} from "./autogen/AutogenLayoutDriverFuelCardQuickInfo";
import {ClickEvent, Component, Snackbar, TableRow, TableRowDisplayEvent, Toast, Label} from "@mcleod/components";
import {Api, ModelRow} from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {CashAdvanceLayoutProps, CashAdvanceSlideout} from "./CashAdvanceSlideout";
import {FuelCardDisplayUtils} from "./FuelCardDisplayUtils";

enum FuelInterface { COMDATA = "C", EFS = "H" }

interface FuelCardProps {
    statusCode: string;
    statusDesc: string;
    imageName: ImageName;
    tooltip: string;
    onClick: string;
    visible: boolean;
    enabled: boolean;
}

export class DriverFuelCardQuickInfo extends AutogenLayoutDriverFuelCardQuickInfo {

    tableDriverManagementOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const data: ModelRow = tableRow.data;

        const hasActiveIfuelControlRecord = data?.get("ifuel_control.is_active", false) === "Y";

        if (data.getBoolean("has_interactive_card", false)
            && data.get("has_fuel_funded_vendor", false)
            && hasActiveIfuelControlRecord) {
            const currentCardStatus = data.get("card_status");
            const cardInterfaceId = data.get("fuel_interface_id");
            const fuelCardImage = tableRow.findComponentById("buttonFuelCard");
            const cashAdvanceImage = tableRow.findComponentById("buttonCashAdvance");
            const hasValidIFuelUser = data?.get("has_valid_ifuel_user", false);

            const fuelCardProps: FuelCardProps = this.getPropsForInterfaceAndStatus(cardInterfaceId, currentCardStatus, hasValidIFuelUser)

            fuelCardImage.setProps({
                tooltip: fuelCardProps.tooltip,
                visible: fuelCardProps.visible,
                enabled: fuelCardProps.enabled,
                onClick: fuelCardProps.onClick,
                imageName: fuelCardProps.imageName,
            });

            const shouldDisplayCashAdvanceIcon: boolean = this.shouldDisplayCashAdvanceIcon(currentCardStatus, hasValidIFuelUser);
            cashAdvanceImage.visible = shouldDisplayCashAdvanceIcon;
            cashAdvanceImage.enabled = shouldDisplayCashAdvanceIcon;

            const cardNum = data.getString("id");
            if (cardNum) {
                const maskedCardNum = this.maskCardNumber(cardNum);
                const cardNumComponent = tableRow.findComponentById("textboxCardNumber");
                cardNumComponent.setProps({
                    text: maskedCardNum
                });
            }

            const cardStatusDescComponent = tableRow.findComponentById("textboxCardStatus");
            cardStatusDescComponent.setProps({
                text: fuelCardProps.statusDesc
            });

        } else {
            const content = tableRow.findComponentById("panelContent");
            content?.setProps({ visible: false });
            this.showInvalidFuelControlWarning(tableRow, hasActiveIfuelControlRecord);
            return;
        }
    }

    private shouldDisplayCashAdvanceIcon(cardStatus: string, hasValidIfuelUser: boolean): boolean {
        return FuelCardDisplayUtils.shouldDisplayDriverCashAdvanceIcon(cardStatus, hasValidIfuelUser);
    }

    async fuelCardIconOnClick(event: ClickEvent) {

        this.collapseNow();

        const progressSnackbar: Snackbar = Snackbar.showDownloadSnackbar("Updating Status", "Updating fuel card status...", {
            persist: true,
            targetPanel: this.parent.getParentLayout(),
        });

        const comp = event.target as Component;
        if (!comp) return;
        const tableRow = TableRow.getContainingTableRow(comp);
        if (tableRow == null) return;
        const modelRow = tableRow.data;

        const cardId = modelRow.get("id");
        const accountId = modelRow.get("account_id");
        const subAccount = modelRow.get("sub_account");
        const fuelInterfaceId = modelRow.get("fuel_interface_id");
        const driverId = modelRow.get("driver_id");
        const payeeId = modelRow.get("payee_id");

        const currentStatus = modelRow.get("card_status");

        if (FuelInterface.EFS === fuelInterfaceId) {
            await this.postNewStatusForTCH(cardId, accountId, subAccount, fuelInterfaceId, currentStatus, driverId, payeeId);
        } else if (FuelInterface.COMDATA === fuelInterfaceId) {
            await this.postNewStatusForComData(driverId, payeeId, accountId, subAccount, fuelInterfaceId, currentStatus, cardId);
        }

        progressSnackbar.dismiss();
    }

    async postNewStatusForTCH(
        cardId: string,
        accountId: string,
        subAccount: string,
        fuelInterfaceId: string,
        cardStatus: string,
        driverId: string,
        payeeId: string) {
        try {
            const response = await Api.search("lme/dispatch/asset/fuel-card-activate-block",
                {
                    card_number: cardId,
                    account_id: accountId,
                    sub_account_id: subAccount,
                    fuel_interface_id: fuelInterfaceId,
                    card_status: cardStatus,
                    driver_id: driverId,
                    payee_id: payeeId,
                });
            if (response) {
                const message = response.data[0].response;
                const actionComplete = response.data[0].action_complete;
                if (actionComplete) {
                    Toast.showToast(message);
                } else {
                    Snackbar.showWarningSnackbar(message);
                }
            }
        } catch (error) {
            Snackbar.showWarningSnackbar(error.messages[0]);
        }
    }

    async postNewStatusForComData(driverId: string, payeeId: string, accountId: string, subAccountId: string, fuelInterfaceId: string, cardStatus: string, fuelCardNumber: string) {
        const endpointInputs = {
            "driver_id": driverId,
            "payee_id": payeeId,
            "fuel_interface_id": fuelInterfaceId,
            "card_status": cardStatus,
            "card_number": fuelCardNumber,
            "account_id": accountId,
            "sub_account_id": subAccountId
        };
        try {
            const response = await Api.search("lme/dispatch/asset/fuel-card-purchase-on-off", endpointInputs);
            if (response) {
                const message = response.data[0].response;
                const actionComplete = response.data[0].action_complete;
                if (actionComplete) {
                    Toast.showToast(message);
                } else {
                    Snackbar.showWarningSnackbar(message);
                }
            }
        } catch (error) {
            Snackbar.showWarningSnackbar(error.messages[0]);
        }
    }

    buttonCashAdvanceOnClick(event: ClickEvent) {
        const component = event.target as Component;
        const activeRow = TableRow.getContainingTableRow(component);
        if (activeRow == null) return;
        const data = activeRow.data;

        const cashAdvanceLayoutProps: CashAdvanceLayoutProps = {
            driverId: data.get("driver_id"),
            driverFullName: `${data.get("driver.first_name")} ${data.get("driver.name")}`,
            cardNum: data.get("id"),
            vendorInterfaceId: data.get("fuel_interface_id"),
            vendorDesc: data.get("fuel_interface.descr"),
            accountId: data.get("account_id"),
            subAccountId: data.get("sub_account"),
            payeeId: data.get("payee_id"),
            tractorId: data.get("tractor_id"),
            assigneeType: data.get("assignee_type"),
            cardStatus: data.get("card_status"),
        }

        CashAdvanceSlideout.showSlideout(cashAdvanceLayoutProps);
    }

    private showInvalidIFuelUserWarning(): void {
        Snackbar.showWarningSnackbar("Cannot change status of card. No valid IFuel user found.");
    }

    public getPropsForInterfaceAndStatus(interfaceId: string, currentStatusCode: string, hasValidIFuelUser: boolean): FuelCardProps {
        const props: FuelCardProps = {
            statusCode: null,
            statusDesc: null,
            imageName: null,
            tooltip: null,
            onClick: null,
            enabled: false,
            visible: false,
        };

        if (FuelInterface.COMDATA === interfaceId) {
            if (currentStatusCode === "A") {
                props.statusCode = currentStatusCode;
                props.statusDesc = "Active";
                props.imageName = ImageName.fuelCardActivate;
                props.tooltip = hasValidIFuelUser ? "Current status: 'Active'\n\nSet status to 'No company purchase'" : "No valid IFuel User found. Card status cannot be changed";
                props.onClick = hasValidIFuelUser ? "fuelCardIconOnClick" : "showInvalidIFuelUserWarning";
                props.enabled = true;
                props.visible = true;
            } else if (currentStatusCode === "B" || currentStatusCode === "I") {
                props.statusCode = currentStatusCode;
                props.statusDesc = currentStatusCode === "B" ? "Blocked" : "No company purchase";
                props.imageName = ImageName.fuelCardBlock;
                props.tooltip = hasValidIFuelUser ? `Current status: ${props.statusDesc}\n\nSet status to 'Active'` : "No valid IFuel User found. Card status cannot be changed";
                props.onClick = hasValidIFuelUser ? "fuelCardIconOnClick" : "showInvalidIFuelUserWarning";
                props.enabled = true;
                props.visible = true;
            } else if (currentStatusCode === "L") {
                props.statusCode = "L";
                props.statusDesc = "Lost";
            } else if (currentStatusCode === "C") {
                props.statusCode = "C";
                props.statusDesc = "Cloned";
            } else if (currentStatusCode === "D") {
                props.statusCode = "D";
                props.statusDesc = "Deleted";
            } else if (currentStatusCode === "X") {
                props.statusCode = "X";
                props.statusDesc = "Permanently blocked";
            } else if (currentStatusCode === "F") {
                props.statusCode = "F";
                props.statusDesc = "Fraud";
            } else if (currentStatusCode === "S") {
                props.statusCode = "S";
                props.statusDesc = "Stolen";
            }

        } else if (FuelInterface.EFS === interfaceId) {
            if (currentStatusCode === "A") {
                props.statusCode = currentStatusCode;
                props.statusDesc = "Active";
                props.imageName = ImageName.fuelCardActivate;
                props.tooltip = hasValidIFuelUser ? "Current status: Active\n\nSet status to 'Hold' and remove overrides" : "No valid IFuel User found. Card status cannot be changed";
                props.onClick = hasValidIFuelUser ? "fuelCardIconOnClick" : "showInvalidIFuelUserWarning";
                props.enabled = true;
                props.visible = true;
            } else if (currentStatusCode === "I" || currentStatusCode === "H") {
                props.statusCode = currentStatusCode;
                props.statusDesc = currentStatusCode === "I" ? "Inactive" : "Hold";
                props.imageName = ImageName.fuelCardBlock;
                props.tooltip = hasValidIFuelUser ? `Current status: ${props.statusDesc}\n\nSet status to 'Active'` : "No valid IFuel User found. Card status cannot be changed";
                props.onClick = hasValidIFuelUser ? "fuelCardIconOnClick" : "showInvalidIFuelUserWarning";
                props.enabled = true;
                props.visible = true;
            } else if (currentStatusCode === "D") {
                props.statusCode = "D";
                props.statusDesc = "Deleted";
            } else if (currentStatusCode === "F") {
                props.statusCode = "F";
                props.statusDesc = "Fraud";
            } else if (currentStatusCode === "C") {
                props.statusCode = "C";
                props.statusDesc = "Cloned";
            }
        }
        return props;
    }

    hasActiveIfuelControlRecord(data: ModelRow): boolean {
        return data?.getBoolean("has_active_ifuel_control", false)
    }

    showInvalidFuelControlWarning(tableRow: TableRow, hasActiveIfuelControlRecord: boolean) {
        const labelMissingIfuelControl = tableRow.findComponentById("labelMissingIfuelControl") as Label;
        labelMissingIfuelControl.visible = !hasActiveIfuelControlRecord;
        labelMissingIfuelControl.text = hasActiveIfuelControlRecord
            ? null
            : `An active Interactive Fuel Control record was not found for account ` +
            `[${tableRow.data?.getString("account_id")}] and subaccount [${tableRow.data?.getString("sub_account")}]` +
            ` for fuel card ${this.maskCardNumber(tableRow.data.get("id"))}`;
    }

    private maskCardNumber(cardNumber: string): string {
        if (!cardNumber) {
            return "";
        }
        return cardNumber.replace(/\d(?=\d{6})/g, '*');
    }
}
